import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import AdminDashboard from "./pages/AdminDashboard";
import CompanyDashboard from "./pages/CompanyDashboard";
import AssociationDashboard from "./pages/AssociationDashboard";
import EcommerceDashboard from "./pages/EcommerceDashboard";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import SelectRole from "./pages/SelectRole";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const checkAuth = async () => {
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem('token');
        setToken(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData, authToken) => {
    setUser(userData);
    if (authToken) {
      localStorage.setItem('token', authToken);
      setToken(authToken);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    localStorage.removeItem('token');
    setToken(null);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return { user, loading, login, logout, checkAuth, token };
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, auth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = auth;

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate('/login', { replace: true });
      } else if (allowedRoles && !allowedRoles.includes(user.role)) {
        // Redirect to appropriate dashboard
        const dashboards = {
          admin: '/admin',
          company: '/company',
          association: '/association',
          ecommerce: '/ecommerce',
          employee: '/employee'
        };
        navigate(dashboards[user.role] || '/', { replace: true });
      }
    }
  }, [user, loading, navigate, allowedRoles]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-800 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-600 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;
  if (allowedRoles && !allowedRoles.includes(user.role)) return null;

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  const auth = useAuth();

  // CRITICAL: Check for session_id SYNCHRONOUSLY during render
  // This prevents race conditions - process new auth BEFORE checking existing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback auth={auth} />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage auth={auth} />} />
      <Route path="/login" element={<LoginPage auth={auth} />} />
      <Route path="/register" element={<RegisterPage auth={auth} />} />
      <Route path="/select-role" element={
        <ProtectedRoute auth={auth} allowedRoles={['company', 'association', 'ecommerce', 'employee']}>
          <SelectRole auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <ProtectedRoute auth={auth} allowedRoles={['admin']}>
          <AdminDashboard auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/company/*" element={
        <ProtectedRoute auth={auth} allowedRoles={['company']}>
          <CompanyDashboard auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/association/*" element={
        <ProtectedRoute auth={auth} allowedRoles={['association']}>
          <AssociationDashboard auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/ecommerce/*" element={
        <ProtectedRoute auth={auth} allowedRoles={['ecommerce']}>
          <EcommerceDashboard auth={auth} />
        </ProtectedRoute>
      } />
      <Route path="/employee/*" element={
        <ProtectedRoute auth={auth} allowedRoles={['employee', 'company']}>
          <EmployeeDashboard auth={auth} />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
